import React from 'react';
import styles from './Timeline.module.css';

const formatTime = (seconds: number) => {
  const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return `${mins}:${secs}s`;
};

interface TimelineProps {
  endTime: number;
  step: number;
  pixelsPerSecond?: number;
  intervals?: number;
}

const isCloseToMultiple = (value: number, step: number, epsilon = 1e-6) => {
  const remainder = value % step;
  return remainder < epsilon || Math.abs(remainder - step) < epsilon;
};

const Timeline: React.FC<TimelineProps> = ({
  endTime,
  step,
  pixelsPerSecond = 10,
  intervals = 4,
}) => {
  const totalWidth = endTime * pixelsPerSecond;

  // Major ticks
  const majorTicks: number[] = [];
  for (let t = 0; t <= endTime; t += step) {
    majorTicks.push(t);
  }

  // If endTime is exactly on a major tick, ensure it's included
  if (
    majorTicks[majorTicks.length - 1] !== endTime &&
    Math.abs(endTime % step) < 1e-6
  ) {
    majorTicks.push(endTime);
  }

  const subtickStep = step / intervals;
  const subticks: number[] = [];

  for (let t = subtickStep; t <= endTime; t += subtickStep) {
    const rounded = parseFloat(t.toFixed(6));
    if (rounded > endTime) continue; // ✅ allow t === endTime
    if (isCloseToMultiple(rounded, step)) continue; // skip if it's a major tick
    subticks.push(rounded);
  }

  return (
    <div className={styles.timeline} style={{width: `${totalWidth}px`}}>
      {/* Major ticks */}
      {majorTicks.map((time, index) => (
        <div
          key={`tick-${index}`}
          className={styles.tick}
          style={{left: `${time * pixelsPerSecond}px`}}>
          <span className={styles.label}>{formatTime(time)}</span>
          <span className={styles.tickMark}></span>
        </div>
      ))}

      {/* Subticks */}
      {subticks.map((time, index) => (
        <div
          key={`subtick-${index}`}
          className={styles.subTick}
          style={{left: `${time * pixelsPerSecond}px`}}>
          &#9679;
        </div>
      ))}
    </div>
  );
};

export default Timeline;
